import React, { FC, ReactNode } from 'react'
import classNames from 'classnames'
import truncate from 'lodash/truncate'
import moment from 'moment'
import SwitchIcon from 'transfix-icons/build/SwitchIcon'
import {
  IFormattedDatetimeProps,
  FormattedDateTime,
} from 'transfix-ui/components/FormattedDateTime'
import { Box, Typography, makeStyles, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IFile, IBaseFileType, TPodState } from 'apps/fleet-planner/types'
import { CheckFilledIcon, RejectedFilledIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { UploadInputButton } from '../atoms/UploadInputButton'

interface IDefaultDocumentSubtitleProps {
  /**
   * @default 'Uploaded'
   */
  text?: string
  createdAt: IBaseFileType['createdAt']
  /**
   * Optional custom props for child <FormattedDateTime /> element
   */
  FormattedDateTimeProps?: Partial<IFormattedDatetimeProps>
}

export const DefaultDocumentSubtitle: FC<IDefaultDocumentSubtitleProps> = ({
  text = 'Uploaded',
  createdAt = '',
  FormattedDateTimeProps = {},
}) => {
  return (
    <>
      {text}{' '}
      <FormattedDateTime
        inSentence
        showTimezone={false}
        datetime={createdAt}
        format='dateAndYear'
        displayTimezone={moment.tz.guess() as AllowedTimezones}
        {...FormattedDateTimeProps}
      />
    </>
  )
}

/**
 * Returns a truncated file name with the original
 * file extension, when applicable
 * @example 'LongNameHere...pdf'
 */
const getTruncatedFileName = (fileName: string = ''): string => {
  if (!fileName) return ''
  const [fileNameNew, extension] = fileName.split('.') || []
  let truncatedName = truncate(fileNameNew, { length: 60 })
  if (extension) truncatedName += `.${extension}`
  return truncatedName
}

interface IDocumentListItemProps<T extends IBaseFileType> {
  document: T
  isLoading?: boolean
  /**
   * @default false
   */
  isLastChild: boolean
  /**
   * @example 'Rate Confirmation'
   */
  documentName: string
  /**
   * @default false
   */
  isExternal?: boolean
  /**
   * podState is used to determine the icon and subtitle
   */
  podState?: TPodState
  /**
   * Pass custom props to <FormattedDateTime />
   */
  FormattedDateTimeProps?: Partial<IFormattedDatetimeProps>
  /**
   * When undefined, this disables replacing, and
   * does not render a replace/upload component
   * @default undefined
   */
  onReplace?: (files: IFile[]) => void
  /**
   * Allows for custom title based on document properties
   * @default ({ filename = '' }) => filename
   */
  getDocumentTitle?: (document: T) => string
  /**
   * Allows for custom author based on document properties
   * @default () => 'Signed by UserNameHere' // todo: add actual values
   */
  getDocumentSubtitle?: (document: T) => ReactNode
}

type TClassKey = 'root'

const useDocumentListItemStyles = makeStyles(() =>
  createStyles<TClassKey, { isLastChild: boolean }>({
    root: {
      background: brandColors.white,
      display: 'grid',
      width: '100%',
    },
  })
)

/**
 * List item for <DocumentList />
 */
export function DocumentListItem<T extends IBaseFileType>(props: IDocumentListItemProps<T>) {
  const {
    document: documentProp,
    onReplace,
    isLoading,
    podState,
    getDocumentTitle = ({ filename = '' }) => filename,
    getDocumentSubtitle = ({ createdAt = '' }) => (
      <DefaultDocumentSubtitle text='Uploaded' createdAt={createdAt} />
    ),
  } = props || {}
  const classes = useDocumentListItemStyles(props)
  const documentTitle = getDocumentTitle(documentProp)
  const documentSubtitle = getDocumentSubtitle(documentProp)
  const truncatedDocumentTitle = getTruncatedFileName(documentTitle)

  const documentUrl = documentProp?.url || documentProp?.fileUrl

  const movePodWarningToTopInCarrierAppEnabled = useFeatureFlag('movePodWarningToTopInCarrierApp')
  return (
    <Box className={classNames(classes.root, 'document-list-item')} data-test='document-list-item'>
      <Box flex='1 1 auto' display='block' alignItems='center'>
        {isLoading && (
          <Box flex='0 0 auto' height={44} display='flex' alignItems='center'>
            <Typography color='textSecondary' variant='subtitle2'>
              Uploading file...
            </Typography>
          </Box>
        )}
        {document && !isLoading && (
          <Box flex='0 0 auto'>
            <Box display='flex' justifyContent='space-between' mb={0.5}>
              <Typography color='primary' variant='body2' component='div'>
                {documentUrl ? (
                  <a href={documentUrl} target='_blank' rel='noopener noreferrer'>
                    {truncatedDocumentTitle}
                  </a>
                ) : (
                  <div>{truncatedDocumentTitle || 'Untitled Attachment'}</div>
                )}
              </Typography>
              {movePodWarningToTopInCarrierAppEnabled && podState && podState !== 'pod_pending' && (
                <Box display='flex' alignItems='center' color={brandColors.coolGray6}>
                  <Box mr={0.5} height='max-content'>
                    <span>{capitalize(podState.replace('pod_', '').replace('in_', 'in '))}</span>
                  </Box>
                  {podState === 'pod_in_review' && (
                    <CheckFilledIcon size='large' color='coolGray4' />
                  )}
                  {podState === 'pod_approved' && <CheckFilledIcon size='large' color='green2' />}
                  {podState === 'pod_rejected' && <RejectedFilledIcon size='large' color='error' />}
                </Box>
              )}
            </Box>
            <Typography variant='subtitle2' color='textSecondary'>
              {documentSubtitle}
            </Typography>
          </Box>
        )}
      </Box>
      {documentProp && onReplace && (
        <Box flex='0 0 auto' display='flex' alignItems='center' justifyContent='end'>
          <UploadInputButton
            ButtonProps={{
              startIcon: <SwitchIcon />,
              color: 'secondary',
            }}
            disabled={isLoading}
            onUpload={onReplace}
            label='Replace File'
          />
        </Box>
      )}
    </Box>
  )
}
